import { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import { useRouter } from 'expo-router';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { handleToUsername } from '@/lib/types/publicProfile';
import { UserPlus, Users, Search, Swords } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Profile } from '@/types/database';
import CreateDuelWizard from '@/components/CreateDuelWizard';
import { FLAGS } from '@/lib/featureFlags';

interface Friend {
  id: string;
  follower_id: string;
  following_id: string;
  created_at: string;
  profile: Profile;
}

export default function FriendsScreen() {
  const router = useRouter();
  const { profile } = useAuth();
  const { theme } = useTheme();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'friends' | 'search'>('friends');
  const [showDuelWizard, setShowDuelWizard] = useState(false);
  const [selectedOpponentId, setSelectedOpponentId] = useState<string | null>(null);

  // V1: Friends disabled — redirect to home
  useEffect(() => {
    if (!FLAGS.V1_FRIENDS_ENABLED) {
      router.replace('/(tabs)/home');
    }
  }, [router]);

  useEffect(() => {
    if (activeTab === 'friends') {
      loadFriends();
    }
  }, [activeTab]);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      searchUsers();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const loadFriends = async () => {
    if (!profile) return;

    const { data } = await supabase
      .from('follows')
      .select('id, follower_id, following_id, created_at')
      .eq('follower_id', profile.id)
      .order('created_at', { ascending: false });

    if (data && data.length > 0) {
      const ids = data.map((f: any) => f.following_id).filter(Boolean);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, handle, display_name, gym_id, avatar_url')
        .in('id', ids as any);
      const profileMap: Record<string, any> = {};
      (profiles || []).forEach((p: any) => {
        profileMap[p.id] = {
          ...p,
          handle: p.handle,
        };
      });
      const merged = data.map((f: any) => ({
        ...f,
        profile: profileMap[f.following_id] ?? { id: f.following_id, display_name: 'User', handle: null },
      }));
      setFriends(merged as any);
    } else {
      setFriends([]);
    }
    setLoading(false);
  };

  const searchUsers = async () => {
    if (!profile) return;

  const { data } = await supabase
      .from('profiles')
      .select('id, handle, display_name, gym_id, avatar_url')
      .or(`handle.ilike.%${searchQuery}%,display_name.ilike.%${searchQuery}%`)
      .neq('id', profile.id)
      .limit(20);

    if (data) {
      const { data: followingData } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', profile.id);

      const followingIds = new Set(followingData?.map(f => f.following_id) || []);

      setSearchResults(
        data.map((p: any) => ({
          ...p,
          username: handleToUsername(p.handle),
          isFollowing: followingIds.has(p.id),
        }))
      );
    }
  };

  const followUser = async (userId: string) => {
    if (!profile) return;

    await supabase.from('follows').insert({
      follower_id: profile.id,
      following_id: userId,
    });

    setSearchResults(prev =>
      prev.map(p => (p.id === userId ? { ...p, isFollowing: true } : p))
    );

    if (activeTab === 'friends') {
      loadFriends();
    }
  };

  const unfollowUser = async (userId: string) => {
    if (!profile) return;

    await supabase
      .from('follows')
      .delete()
      .eq('follower_id', profile.id)
      .eq('following_id', userId);

    setFriends(prev => prev.filter(f => f.following_id !== userId));
    setSearchResults(prev =>
      prev.map(p => (p.id === userId ? { ...p, isFollowing: false } : p))
    );
  };

  const handleChallengeToDuel = (userId: string) => {
    setSelectedOpponentId(userId);
    setShowDuelWizard(true);
  };

  const handleDuelCreated = () => {
    setSelectedOpponentId(null);
  };

  const renderFriend = (friend: Friend) => (
    <View
      key={friend.id}
      style={[
        styles.friendCard,
        { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
      ]}>
      <View style={styles.friendInfo}>
        <View style={[styles.avatar, { backgroundColor: theme.colors.accent }]}>
          <Text style={[styles.avatarText, { color: theme.colors.accentForeground }]}>
            {friend.profile.display_name.charAt(0).toUpperCase()}
          </Text>
        </View>
        <View style={styles.friendDetails}>
          <Text style={[styles.friendName, { color: theme.colors.text }]}>
            {friend.profile.display_name}
          </Text>
          <Text style={[styles.friendUsername, { color: theme.colors.textSecondary }]}>
            @{friend.profile.handle}
          </Text>
        </View>
      </View>
      <View style={styles.friendActions}>
        <TouchableOpacity
          style={[
            styles.actionButton,
            { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.accent },
          ]}
          onPress={() => handleChallengeToDuel(friend.following_id)}>
          <Swords size={18} color={theme.colors.accent} />
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.unfollowButton,
            { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border },
          ]}
          onPress={() => unfollowUser(friend.following_id)}>
          <Text style={[styles.unfollowText, { color: theme.colors.textSecondary }]}>
            Unfollow
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderSearchResult = (user: any) => (
    <View
      key={user.id}
      style={[
        styles.friendCard,
        { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
      ]}>
      <View style={styles.friendInfo}>
        <View style={[styles.avatar, { backgroundColor: theme.colors.accent }]}>
          <Text style={[styles.avatarText, { color: theme.colors.accentForeground }]}>
            {user.display_name.charAt(0).toUpperCase()}
          </Text>
        </View>
        <View style={styles.friendDetails}>
          <Text style={[styles.friendName, { color: theme.colors.text }]}>
            {user.display_name}
          </Text>
          <Text style={[styles.friendUsername, { color: theme.colors.textSecondary }]}>
            @{user.username}
          </Text>
        </View>
      </View>
      {user.isFollowing ? (
        <TouchableOpacity
          style={[
            styles.followingButton,
            { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border },
          ]}
          onPress={() => unfollowUser(user.id)}>
          <Text style={[styles.followingText, { color: theme.colors.textSecondary }]}>
            Following
          </Text>
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          style={[styles.followButton, { backgroundColor: theme.colors.accent }]}
          onPress={() => followUser(user.id)}>
          <UserPlus size={18} color={theme.colors.text} />
          <Text style={[styles.followText, { color: theme.colors.accentForeground }]}>Follow</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <LinearGradient
        colors={[theme.colors.background, theme.colors.surfaceVariant]}
        style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <View style={styles.headerTop}>
          <View style={{ width: 28 }} />
          <Text style={[styles.headerTitle, { color: theme.colors.accent }]}>FRIENDS</Text>
          <View style={{ width: 28 }} />
        </View>

        <View style={styles.tabs}>
          <TouchableOpacity
            style={[
              styles.tab,
              activeTab === 'friends' && styles.activeTab,
              { borderBottomColor: theme.colors.accent },
            ]}
            onPress={() => setActiveTab('friends')}>
            <Users
              size={18}
              color={activeTab === 'friends' ? theme.colors.accent : theme.colors.textSecondary}
            />
            <Text
              style={[
                styles.tabText,
                {
                  color:
                    activeTab === 'friends' ? theme.colors.accent : theme.colors.textSecondary,
                },
              ]}>
              My Friends
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.tab,
              activeTab === 'search' && styles.activeTab,
              { borderBottomColor: theme.colors.accent },
            ]}
            onPress={() => setActiveTab('search')}>
            <Search
              size={18}
              color={activeTab === 'search' ? theme.colors.accent : theme.colors.textSecondary}
            />
            <Text
              style={[
                styles.tabText,
                {
                  color: activeTab === 'search' ? theme.colors.accent : theme.colors.textSecondary,
                },
              ]}>
              Find Friends
            </Text>
          </TouchableOpacity>
        </View>

        {activeTab === 'search' && (
          <View style={[styles.searchContainer, { backgroundColor: theme.colors.surface }]}>
            <Search size={20} color={theme.colors.textSecondary} />
            <TextInput
              style={[styles.searchInput, { color: theme.colors.text }]}
              placeholder="Search users..."
              placeholderTextColor={theme.colors.textTertiary}
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoCapitalize="none"
            />
          </View>
        )}
      </LinearGradient>

      {loading && activeTab === 'friends' ? (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={theme.colors.accent} />
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.content}>
          {activeTab === 'friends' ? (
            friends.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Users size={48} color={theme.colors.textTertiary} />
                <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
                  No friends yet
                </Text>
                <Text style={[styles.emptySubtext, { color: theme.colors.textTertiary }]}>
                  Search for users to start following
                </Text>
              </View>
            ) : (
              friends.map(renderFriend)
            )
          ) : searchQuery.length < 2 ? (
            <View style={styles.emptyContainer}>
              <Search size={48} color={theme.colors.textTertiary} />
              <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
                Search for users
              </Text>
              <Text style={[styles.emptySubtext, { color: theme.colors.textTertiary }]}>
                Enter at least 2 characters
              </Text>
            </View>
          ) : (
            searchResults.map(renderSearchResult)
          )}
        </ScrollView>
      )}

      <CreateDuelWizard
        visible={showDuelWizard}
        onClose={() => setShowDuelWizard(false)}
        onDuelCreated={handleDuelCreated}
        preselectedOpponentId={selectedOpponentId || undefined}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    paddingTop: 60,
    paddingBottom: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '900',
    letterSpacing: 2,
  },
  tabs: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomWidth: 2,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '700',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 2,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
  },
  content: {
    padding: 16,
  },
  friendCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 12,
  },
  friendInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontSize: 20,
    fontWeight: '900',
  },
  friendDetails: {
    flex: 1,
  },
  friendName: {
    fontSize: 16,
    fontWeight: '700',
  },
  friendUsername: {
    fontSize: 14,
    marginTop: 2,
  },
  friendActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    width: 40,
    height: 40,
    borderRadius: 0,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  unfollowButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 0,
    borderWidth: 1,
  },
  unfollowText: {
    fontSize: 13,
    fontWeight: '600',
  },
  followButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 0,
  },
  followText: {
    fontSize: 13,
    fontWeight: '700',
  },
  followingButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 0,
    borderWidth: 1,
  },
  followingText: {
    fontSize: 13,
    fontWeight: '600',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 60,
    gap: 12,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '700',
  },
  emptySubtext: {
    fontSize: 14,
  },
});
